import { useEffect, useRef } from 'react';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { useFpId } from '../../../../../hooks/useFpId';
import { SocialSecurityStrategy, SocialSecurityWhatIf } from './api/types';
import { useSocialSecurityProbabilityQuery } from './api/useSocialSecurityProbabilityQuery';
import { useSocialSecurityProbabilityWithWhatIfsMutation } from './api/useSocialSecurityProbabilityWithWhatIfsMutation';
import { useSocialSecurityWhatIfRefreshToken } from './api/useSocialSecurityWhatIfRefresh';

interface Params {
  isWhatIfsActive?: boolean;
  whatIfsBody?: SocialSecurityWhatIf[];
}

// Hooks rules require fixed call order, so each strategy gets its own hook invocation.
// We use one hook per strategy below; the aggregator then maps results into the table-friendly shape.
const useStrategyProbability = (
  strategy: SocialSecurityStrategy,
  isWhatIfsActive: boolean,
  whatIfsBody: SocialSecurityWhatIf[],
) => {
  const clientId = useClientId();
  const fpId = useFpId();
  const whatIfRefreshToken = useSocialSecurityWhatIfRefreshToken(clientId, fpId);

  const query = useSocialSecurityProbabilityQuery({
    clientId,
    fpId,
    strategy,
    enabled: !isWhatIfsActive,
  });

  const mutation = useSocialSecurityProbabilityWithWhatIfsMutation({
    clientId,
    fpId,
    strategy,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (isWhatIfsActive) {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      mutation.mutate({
        clientId,
        fpId,
        strategy,
        body: whatIfsBody,
        signal: controller.signal,
      });
    }
  }, [isWhatIfsActive, whatIfsBody, clientId, fpId, strategy, whatIfRefreshToken]);

  // The what-ifs mutation fires in a post-mount effect, so on the first render it is
  // still idle (not loading, no data). Treat that idle window as loading to avoid
  // briefly rendering a dash before the mutate call runs.
  const isFetching = isWhatIfsActive ? mutation.isLoading || mutation.isIdle : query.isFetching;
  const probabilityOfSuccess = isWhatIfsActive
    ? mutation.data?.ProbabilityOfSuccess
    : query.data?.ProbabilityOfSuccess;
  // react-query v3 types `error` as `unknown`; authFetch only throws Error/ApiError,
  // so we narrow it once here and let the typed value flow through the rest of the hook.
  const error = (isWhatIfsActive ? mutation.error : query.error) as Error | null;

  return { isFetching, probabilityOfSuccess, error };
};

export const useSocialSecuritySuccessProbabilities = ({
  isWhatIfsActive = false,
  whatIfsBody = [],
}: Params = {}) => {
  // One hook call per strategy keeps React's rules-of-hooks happy.
  const activePlan = useStrategyProbability('ActivePlan', isWhatIfsActive, whatIfsBody);
  const asSoonAsPossible = useStrategyProbability('AsSoonAsPossible', isWhatIfsActive, whatIfsBody);
  const atRetirement = useStrategyProbability('AtRetirement', isWhatIfsActive, whatIfsBody);
  const atFra = useStrategyProbability('AtFra', isWhatIfsActive, whatIfsBody);
  const atAge70 = useStrategyProbability('AtAge70', isWhatIfsActive, whatIfsBody);

  // Order MUST match the table column order (and the C# SocialSecurityProbabilitiesResponse field order).
  const perStrategy = [activePlan, asSoonAsPossible, atRetirement, atFra, atAge70];

  const data = {
    ProbabilityOfSuccess: perStrategy.map(strategy => strategy.probabilityOfSuccess),
  };

  const loadingStates = perStrategy.map(strategy => strategy.isFetching);
  const errors = perStrategy.map(strategy => strategy.error);

  // Aggregate flags for any callers that need a single "is anything loading / has anything errored".
  const isFetching = loadingStates.some(Boolean);
  const firstError = errors.find(error => error !== null) ?? null;

  return {
    isFetching,
    data,
    error: firstError,
    loadingStates,
    errors,
  };
};
